// A stranger's text (a sender's name, an address, a subject) made safe to print as ONE line.
//
// Line breaks and the other kinds of space (U+2028, U+2029, tabs, the no-break space) become one
// space, so a name can never start a second line that looks like the page's own words. Characters
// that draw nothing (zero-width marks, the direction controls that reorder what sits beside them,
// the soft hyphen, other control characters) are taken out whole, so they leave no invisible gap and
// no reordering. The result is trimmed. Every place the support email screens print a stranger's
// name, address or subject goes through here (`oneLineAddress` for an address, which keeps its end when cut).
const AS_SPACE = /[\t\n\v\f\r\u{85}\u{A0}\u{1680}\u{2000}-\u{200A}\u{2028}\u{2029}\u{202F}\u{205F}\u{3000}]/gu;
// Draws nothing, or reorders what is beside it: controls, soft hyphen, the Arabic letter mark, Mongolian and
// zero-width marks (the joiners U+200C and U+200D are handled apart), direction controls, invisible operators,
// the combining grapheme joiner, Hangul and Khmer fillers, the blank Braille cell, variation selectors and tag characters.
const DRAWS_NOTHING = /[\u{0}-\u{1F}\u{7F}-\u{9F}\u{AD}\u{34F}\u{61C}\u{115F}\u{1160}\u{17B4}\u{17B5}\u{180B}-\u{180F}\u{200B}\u{200E}\u{200F}\u{202A}-\u{202E}\u{2060}-\u{206F}\u{2800}\u{3164}\u{FE00}-\u{FE0F}\u{FEFF}\u{FFA0}\u{FFF9}-\u{FFFB}\u{E0000}-\u{E007F}\u{E0100}-\u{E01EF}]/gu;
// The joiners are real parts of some scripts (Persian, Indic) and of emoji sequences: kept only between two letters
// (or two pictographs), taken out anywhere else, where they draw nothing.
const JOINER = /(?<![\p{L}\p{M}\p{Extended_Pictographic}])[\u{200C}\u{200D}]|[\u{200C}\u{200D}](?![\p{L}\p{M}\p{Extended_Pictographic}])/gu;
// Every character of the format category (Cf) draws nothing or reorders what is beside it; only the joiners (below) can matter.
const FORMAT = /(?![\u{200C}\u{200D}])\p{Cf}/gu;
// A very long value is cut here, with three dots, so no stranger can fill a screen or a question with one.
export const ONE_LINE_MAX = 300;

function clean(v: string | null | undefined): string {
	if (typeof v !== 'string') return '';
	return v.replace(AS_SPACE, ' ').replace(DRAWS_NOTHING, '').replace(FORMAT, '').replace(JOINER, '').replace(/ {2,}/g, ' ').trim();
}

export function oneLine(v: string | null | undefined): string {
	const out = clean(v);
	const chars = Array.from(out);
	return chars.length > ONE_LINE_MAX ? `${chars.slice(0, ONE_LINE_MAX).join('').trimEnd()}...` : out;
}

// The longest domain (with its @) an address keeps whole. A real domain is at most 253 characters; a longer one is a
// stranger filling a line, and it is cut at its FRONT so the end, the part a person judges, is always shown.
const DOMAIN_MAX = 253;

// An address is cut in the middle of the part before the last @, never after it: the domain is the half a person
// judges a sender by, so two long addresses that differ only there still read differently. A domain longer than
// DOMAIN_MAX loses its front instead, so the result is never longer than ONE_LINE_MAX.
export function oneLineAddress(v: string | null | undefined): string {
	const out = clean(v);
	if (Array.from(out).length <= ONE_LINE_MAX) return out;
	const at = out.lastIndexOf('@');
	if (at < 0) return oneLine(out);
	const local = Array.from(out.slice(0, at));
	let rest = Array.from(out.slice(at));
	if (rest.length > DOMAIN_MAX) rest = ['@', '.', '.', '.', ...rest.slice(rest.length - (DOMAIN_MAX - 4))];
	const room = ONE_LINE_MAX - rest.length - 3;
	const tail = rest.join('');
	if (local.length <= room) return `${local.join('')}${tail}`;
	const head = Math.ceil(room / 2);
	return `${local.slice(0, head).join('')}...${local.slice(local.length - (room - head)).join('')}${tail}`;
}
